// Every Firestore query the portal makes, in one place. Each one is served
// by an index in firestore.indexes.json (README → "Queries and indexes"):
// change a query here and check its index there.
import { collection, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";

const keys = (productId) => collection(db, productId);
const customers = () => collection(db, "customers");

// distributor filter values: "all" | "stock" (no distributor) | <distributorId>
function distributorClause(distributor) {
  if (!distributor || distributor === "all") return [];
  return [
    where("distributorId", "==", distributor === "stock" ? null : distributor),
  ];
}

// --- Licences (admin) -------------------------------------------------------

export const LICENCE_SORTS = {
  newest: { label: "Newest first", clause: () => orderBy("createdOn", "desc") },
  expiry: { label: "Expiry date", clause: () => orderBy("expirySort", "asc") },
};

// Equality filters only, so Firestore can merge one index per filter field.
export function licenceFilterClauses({
  includeDev,
  status,
  type,
  distributor,
}) {
  return [
    ...(includeDev ? [] : [where("development", "==", false)]),
    ...(status && status !== "all"
      ? [where("displayStatus", "==", status)]
      : []),
    ...(type && type !== "all" ? [where("type", "==", type)] : []),
    ...distributorClause(distributor),
  ];
}

export const licenceListQuery = (productId, filters, sort = "newest") =>
  query(
    keys(productId),
    ...licenceFilterClauses(filters),
    LICENCE_SORTS[sort].clause(),
  );

export const licenceCountQuery = (productId, filters) =>
  query(keys(productId), ...licenceFilterClauses(filters));

// Search returns up to SEARCH_LIMIT matches; other filters apply to those.
export const SEARCH_LIMIT = 200;
export const licenceSearchQuery = (productId, token) =>
  query(
    keys(productId),
    where("searchTokens", "array-contains", token),
    orderBy("createdOn", "desc"),
  );

export const developmentCountQuery = (productId) =>
  query(keys(productId), where("development", "==", true));

export const expiringQuery = (productId) =>
  query(
    keys(productId),
    where("displayStatus", "==", "expiring"),
    orderBy("expirySort", "asc"),
  );

// --- One distributor's keys (distributor pages and admin distributor page) --

export const DISTRIBUTOR_SORTS = {
  issued: {
    label: "Recently issued",
    clause: () => orderBy("issuedAt", "desc"),
  },
  expiry: { label: "Expiry date", clause: () => orderBy("expirySort", "asc") },
};

export const distributorKeysQuery = (
  productId,
  distributorId,
  { status, sort = "issued" } = {},
) =>
  query(
    keys(productId),
    where("distributorId", "==", distributorId),
    ...(status && status !== "all"
      ? [where("displayStatus", "==", status)]
      : []),
    DISTRIBUTOR_SORTS[sort].clause(),
  );

export const distributorKeySearchQuery = (productId, distributorId, token) =>
  query(
    keys(productId),
    where("distributorId", "==", distributorId),
    where("searchTokens", "array-contains", token),
    orderBy("issuedAt", "desc"),
  );

export const distributorKeyCountQuery = (
  productId,
  distributorId,
  extraClauses = [],
) =>
  query(
    keys(productId),
    where("distributorId", "==", distributorId),
    ...extraClauses,
  );

// Keys linked to a customer record. Distributors must also filter on their
// own distributorId for the security rules.
export const customerKeysQuery = (productId, customerId, distributorId) =>
  query(
    keys(productId),
    where("customerId", "==", customerId),
    ...(distributorId ? [where("distributorId", "==", distributorId)] : []),
  );

// Keys that went to a customer (issued by a distributor or generated for a
// LiFE direct customer), optionally between two dates, for trends.
export function issuedKeysCountQuery(productId, distributor, from, to) {
  return query(
    keys(productId),
    where("development", "==", false),
    ...distributorClause(distributor),
    ...(from ? [where("issuedAt", ">=", from)] : []),
    ...(to ? [where("issuedAt", "<", to)] : []),
  );
}

export const issuedKeysFlagCountQuery = (productId, distributor, linkedOnly) =>
  query(
    keys(productId),
    where("development", "==", false),
    where("issued", "==", true),
    ...(linkedOnly ? [where("linked", "==", true)] : []),
    ...distributorClause(distributor),
  );

// --- Customers ----------------------------------------------------------------

// owner: undefined = everyone (admins), null = LiFE direct, or a distributorId
const ownerClause = (owner) =>
  owner === undefined ? [] : [where("distributorId", "==", owner)];

export const customersQuery = (owner) =>
  query(customers(), ...ownerClause(owner), orderBy("createdAt", "desc"));

export const customerSearchQuery = (owner, token) =>
  query(
    customers(),
    ...ownerClause(owner),
    where("searchTokens", "array-contains", token),
    orderBy("createdAt", "desc"),
  );

export function customerCountQuery(owner, from, to) {
  return query(
    customers(),
    ...ownerClause(owner),
    ...(from ? [where("createdAt", ">=", from)] : []),
    ...(to ? [where("createdAt", "<", to)] : []),
  );
}

// Admin page filter value -> owner argument above.
export const ownerFromFilter = (filter) =>
  filter === "all" ? undefined : filter === "direct" ? null : filter;
export const distributorFromOwnerFilter = (filter) =>
  filter === "all" ? "all" : filter === "direct" ? "stock" : filter;
